import useAuthModal from "@/hooks/useAuthModal";
import { Input } from "@jammable/ui-components/Forms/Input";
import axios from "axios";
import { getIdToken, requireIdToken } from "../utils/firebaseAuth";
import Image from "next/image";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useInfiniteQuery, useQueryClient } from "react-query";
import ModelCommentItem from "./ModelCommentItem";
import { Spinner } from "@jammable/ui-components/Loader";
import { Button } from "@jammable/ui-core";
import { Badge } from "@jammable/ui-components/Badge";
import NotFound from "../../../apps/web/components/NotFound";

const ModelCommentSection = ({ user, model }) => {
  const [commenting, setCommenting] = useState(false);
  const { checkAuthAndExecute } = useAuthModal();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState("top");

  // One field, so no Formik/yup (they were 70 KB of every voice page's first
  // load). Same rules as their schema: required, at most 500 characters; an
  // invalid comment just isn't sent (the Input never rendered the error).
  const [comment, setComment] = useState("");
  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment || comment.length > 500) return;
    if (!checkAuthAndExecute()) return false;
    setCommenting(true);

    const token = await requireIdToken();

    axios
      .post(
        `/api/comment/createComment`,
        {
          body: comment,
          modelId: model?._id,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      )
      .then(() => {
        queryClient.invalidateQueries(["comments", model?._id]).then(() => {
          toast.success("Comment posted!");
          setCommenting(false);
          setComment("");
        });
      })
      .catch((err) => {
        setCommenting(false);
        toast.error(err?.response?.data?.message);
      });
  };
  const limit = 5;
  const fetchComments = async ({ pageParam = 0 }) => {
    const token = await getIdToken();
    const res = await axios.get(
      `/api/comment/getCommentsModel?page=${pageParam}`,
      {
        params: {
          modelId: model?._id,
          limit: limit,
          token: token,
          sort: sort,
        },
        headers: {
          Authorization: token,
        },
      },
    );
    const data = res.data;
    return data;
  };

  const commentData = useInfiniteQuery(
    ["comments", model?._id, sort],
    fetchComments,
    {
      getNextPageParam: (lastpage, allpages) => {
        const maxPages = Math.ceil(lastpage?.length / limit);
        const nextPage = allpages.length + 1;
        return nextPage <= maxPages ? nextPage : null;
      },
      refetchOnWindowFocus: true,
      onError: (error) => {},
    },
  );

  const sortOptions = [
    { value: "top", label: "Top" },

    { value: "newest", label: "Newest" },
  ];

  return (
    <section className="tw-flex tw-w-full tw-flex-col tw-gap-3.5">
      <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-jam-ink md:tw-text-[28px]">
        {(model?.comments ?? 0).toLocaleString("en-US")} comments
      </h2>

      <div className="tw-w-full">
        <form
          onSubmit={submitComment}
          className="tw-flex tw-items-start tw-gap-2.5"
        >
          <Input
            name="comment"
            aria-label="Add a comment"
            className="tw-h-[50px] tw-w-full tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[18px] tw-text-sm tw-font-bold tw-text-jam-ink"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment"
          />
          <Button
            disabled={commenting}
            className="tw-flex tw-h-[50px] tw-items-center tw-rounded-full tw-bg-jam-purple tw-px-[22px] tw-text-sm tw-font-extrabold"
            type="submit"
          >
            {commenting ? (
              <Spinner className="tw-border-white" size={4} />
            ) : (
              "Post"
            )}
          </Button>
        </form>

        <div className="tw-mt-3.5 tw-flex tw-gap-1.5">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={sort === option.value}
              onClick={() => {
                setSort(option.value);
              }}
              className={`tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold ${
                sort === option.value
                  ? "tw-bg-jam-ink tw-text-white"
                  : "tw-bg-jam-lav tw-text-jam-ink"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="tw-mt-5 tw-flex tw-flex-col tw-gap-4">
          {commentData?.data?.pages[0]?.comments?.length === 0 && (
            // <div className="tw-font-bold tw-text-center tw-mt-2">
            //   No comments here...
            // </div>
            <NotFound
              title="No comments here..."
              description="Start the conversation!"
              animateIn={false}
            />
          )}

          {commentData?.data?.pages?.map((page) =>
            page?.comments?.map((comment) => {
              return comment?.parentid === null ? (
                <ModelCommentItem
                  key={comment?._id}
                  comment={comment}
                  model={model}
                  page={page}
                />
              ) : null;
            }),
          )}
          {commentData?.isLoading && (
            <div className="tw-w-full tw-flex tw-justify-center">
              <Spinner size={5} className="tw-border-3" />
            </div>
          )}
          {commentData?.hasNextPage && (
            <button
              type="button"
              onClick={() => {
                commentData.fetchNextPage();
              }}
              className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-[18px] tw-py-2.5 tw-text-sm tw-font-extrabold tw-text-jam-ink sm:tw-w-max"
            >
              {commentData?.isFetchingNextPage ? (
                <Spinner size={5} className="tw-border-3 tw-border-jam-ink" />
              ) : (
                "Show more comments"
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ModelCommentSection;
